import React, { useMemo } from "react";

import {
  Coins,
  DollarSign,
  FileText,
  Percent,
  TrendingUp,
} from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Skeleton } from "~/components/ui/skeleton";
import { CreditNoteCost } from "~/hooks/useCreditNoteCosts";
import { FacturaCost } from "~/services/facturaCostsService";
import { SalesRequestDocument } from "~/types/client";
import { formatCurrency } from "~/utils/helpers";

interface FacturasStatisticsProps {
  facturas: SalesRequestDocument[];
  loading: boolean;
  startDate?: Date;
  endDate?: Date;
  userRole?: string;
  currentUserId?: string;
  /** WAC cost per sales_request (keyed by sales_request_id), for cost/margin cards. */
  costMap?: Map<string, FacturaCost>;
  /**
   * Per-line WAC return cost for credit-note (devolución) lines, keyed by
   * `${bsale_number}|${cod_prod}|${bsale_detail_id}`. Netted out of "Costo de
   * Ventas" so the cards reconcile with the "Excel detallado" export.
   */
  creditNoteCostMap?: Map<string, CreditNoteCost>;
}

interface ChartData {
  date: string;
  formattedDate: string;
  count: number;
  amount: number;
}

const FacturasStatistics: React.FC<FacturasStatisticsProps> = ({
  facturas,
  loading,
  startDate,
  endDate,
  userRole,
  currentUserId,
  costMap,
  creditNoteCostMap,
}) => {
  // Calculate statistics directly from facturas data for fast performance
  // Note: facturas are now pre-filtered in useFacturasWithPayments to only include facturas and credit notes
  const statistics = useMemo(() => {
    if (loading || !facturas.length) {
      return {
        totalCount: 0,
        facturasCount: 0,
        creditNotesCount: 0,
        debitNotesCount: 0,
        totalAmount: 0,
        facturasAmount: 0,
        creditNotesAmount: 0,
        debitNotesAmount: 0,
        netAmount: 0,
        averageFacturaAmount: 0,
        costoFacturas: 0,
        costoNotasCredito: 0,
        costoVentas: 0,
        margenBruto: 0,
        margenPct: null as number | null,
        chartData: [] as ChartData[],
      };
    }

    let totalFacturas = 0;
    let totalCreditNotes = 0;
    let totalDebitNotes = 0;
    let totalAmount = 0;
    let creditNotesAmount = 0;
    let debitNotesAmount = 0;
    // Cost of sales (WAC). Facturas add cost; credit notes (devoluciones) net it
    // back out — mirroring the "Excel detallado" export so the two reconcile.
    let costoFacturas = 0;
    const chartDataMap: Record<string, { count: number; amount: number }> = {};

    facturas.forEach((factura) => {
      // Handle different document types with appropriate amount logic
      let amount = 0;
      if (factura.documentType === "nota_de_credito") {
        // For credit notes, prioritize net_amount from the enhanced data
        amount =
          (factura as any).net_amount || (factura as any).total_amount || 0;
      } else if (factura.documentType === "nota_de_debito") {
        // For debit notes, use net_amount (now enhanced by optimized service)
        amount = (factura as any).net_amount || 0;
      } else {
        // For regular facturas, use net_amount from sales_request_documents (BSale values)
        // Fallback to total_amount from sales_request for backwards compatibility
        amount = Math.round(
          (factura as any).net_amount || (factura as any).total_amount || 0
        );
      }

      const date = new Date(factura.createdAt);
      const dateKey = date.toISOString().split("T")[0]; // YYYY-MM-DD

      if (!chartDataMap[dateKey]) {
        chartDataMap[dateKey] = { count: 0, amount: 0 };
      }

      if (factura.documentType === "nota_de_credito") {
        totalCreditNotes++;
        // Credit notes should reduce the total amount
        creditNotesAmount += Math.abs(amount); // Keep positive for display
        totalAmount -= Math.abs(amount); // Subtract from total
      } else if (factura.documentType === "nota_de_debito") {
        totalDebitNotes++;
        // Debit notes should add to the total amount but not be shown in UI
        debitNotesAmount += Math.abs(amount); // Keep positive for display
        totalAmount += Math.abs(amount); // Add to total for "Monto Total Facturado"
      } else {
        totalFacturas++;
        totalAmount += amount;

        // Add this factura's WAC cost. costMap only holds costed sales requests,
        // so uncosted facturas (e.g. pure-service notes) simply add 0.
        // Cancelled (fully-annulled) facturas keep their revenue (it nets with the
        // NC) but must NOT add cost: the goods return to stock and the NC reverses
        // the sale, so counting their WAC would leave cost with no reversal.
        const costEntry =
          factura.salesRequestId && !factura.cancelled
            ? costMap?.get(factura.salesRequestId)
            : undefined;
        if (costEntry && costEntry.costTotal > 0) {
          costoFacturas += costEntry.costTotal;
        }
      }

      chartDataMap[dateKey].count++;
      chartDataMap[dateKey].amount += Math.abs(amount);
    });

    // Generate chart data
    const chartData: ChartData[] = Object.entries(chartDataMap)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, data]) => ({
        date,
        formattedDate: new Date(date).toLocaleDateString("es-ES", {
          day: "2-digit",
          month: "short",
        }),
        count: data.count,
        amount: data.amount,
      }));

    // Calculate separate amounts for display
    const facturasOnlyAmount =
      totalAmount + creditNotesAmount - debitNotesAmount; // Get facturas-only amount (exclude debit notes from breakdown)
    const netAmount = totalAmount; // This is net (facturas + debit notes - credit notes)

    // Calculate average factura amount (only for regular facturas, not credit/debit notes)
    const averageFacturaAmount =
      totalFacturas > 0 ? facturasOnlyAmount / totalFacturas : 0;

    // Credit-note (devolución) return cost — netted out of the cost of sales the
    // same way the "Excel detallado" export does (NC rows carry a negative cost).
    // Administrative NCs have no return movement and aren't in the map.
    let costoNotasCredito = 0;
    if (creditNoteCostMap) {
      for (const nc of creditNoteCostMap.values()) {
        costoNotasCredito += nc.costTotal;
      }
    }

    // Net cost of sales, and net margin over the net facturado (= totalAmount,
    // which nets credit notes and includes service facturas at $0 cost). This ties
    // the cards to the "Monto Total Facturado" headline and to the Excel detallado.
    const costoVentas = costoFacturas - costoNotasCredito;
    const margenBruto = netAmount - costoVentas;
    const margenPct = netAmount !== 0 ? (margenBruto / netAmount) * 100 : null;

    return {
      totalCount: totalFacturas + totalCreditNotes, // Exclude debit notes from total count
      facturasCount: totalFacturas,
      creditNotesCount: totalCreditNotes,
      debitNotesCount: totalDebitNotes,
      totalAmount: netAmount, // Net amount (facturas + debit notes - credit notes) for main display
      facturasAmount: facturasOnlyAmount, // Gross facturas amount for breakdown
      creditNotesAmount,
      debitNotesAmount,
      netAmount: netAmount, // Net amount for compatibility
      averageFacturaAmount, // Average amount per factura
      costoFacturas, // WAC cost of the facturas (gross)
      costoNotasCredito, // WAC return cost of credit notes (netted out)
      costoVentas, // Net WAC cost of sales = costoFacturas - costoNotasCredito
      margenBruto, // netAmount - costoVentas
      margenPct, // margenBruto / netAmount * 100
      chartData,
    };
  }, [facturas, loading, costMap, creditNoteCostMap]);

  // Show loading skeleton
  if (loading) {
    return (
      <div className="mb-4 space-y-4">
        {/* Statistics Cards Skeleton */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(3)].map((_, i) => (
            <Card key={i}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-4 rounded" />
              </CardHeader>
              <CardContent>
                <Skeleton className="mb-2 h-8 w-24" />
                <Skeleton className="h-3 w-20" />
              </CardContent>
            </Card>
          ))}
        </div>
        {/* Chart Skeleton */}
        <Card>
          <CardHeader>
            <Skeleton className="h-5 w-40" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-[200px] w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mb-4 space-y-4">
      {/* Statistics Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {/* Total Documents Count */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Total de Documentos
            </CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{statistics.totalCount}</div>
            <div className="mt-2 space-y-1 text-xs text-muted-foreground">
              <div className="flex justify-between">
                <span>Facturas:</span>
                <span className="font-medium">{statistics.facturasCount}</span>
              </div>
              <div className="flex justify-between">
                <span>Notas de Crédito:</span>
                <span className="font-medium">
                  {statistics.creditNotesCount}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Total Amount */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Monto Total Facturado (sin IVA)
            </CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              ${formatCurrency(statistics.totalAmount)}
            </div>
            <div className="mt-2 space-y-1 text-xs text-muted-foreground">
              <div className="flex justify-between">
                <span>Facturas:</span>
                <span className="font-medium">
                  ${formatCurrency(statistics.facturasAmount)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Notas Crédito:</span>
                <span className="font-medium text-red-600">
                  -${formatCurrency(statistics.creditNotesAmount)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Average Factura Amount */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Promedio por Factura (sin IVA)
            </CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              ${formatCurrency(statistics.averageFacturaAmount)}
            </div>
            <div className="mt-2 text-xs text-muted-foreground">
              Monto promedio por factura
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Cost & Margin section — hidden from sellers; data under review, not official */}
      {userRole !== "seller" && (
        <div className="space-y-3">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {/* Cost of Sales (WAC) */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Costo de Ventas (WAC)
                </CardTitle>
                <Coins className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  ${formatCurrency(Math.round(statistics.costoVentas))}
                </div>
                <div className="mt-2 space-y-1 text-xs text-muted-foreground">
                  <div className="flex justify-between">
                    <span>Costo facturas:</span>
                    <span className="font-medium">
                      ${formatCurrency(Math.round(statistics.costoFacturas))}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Notas Crédito:</span>
                    <span className="font-medium text-red-600">
                      -$
                      {formatCurrency(Math.round(statistics.costoNotasCredito))}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Gross Margin */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Margen Bruto
                </CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div
                  className={`text-2xl font-bold ${
                    statistics.margenBruto < 0 ? "text-red-600" : ""
                  }`}
                >
                  ${formatCurrency(Math.round(statistics.margenBruto))}
                </div>
                <div className="mt-2 text-xs text-muted-foreground">
                  Facturación neta − Costo de ventas
                </div>
              </CardContent>
            </Card>

            {/* Margin % */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Margen %</CardTitle>
                <Percent className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div
                  className={`text-2xl font-bold ${
                    (statistics.margenPct ?? 0) < 0 ? "text-red-600" : ""
                  }`}
                >
                  {statistics.margenPct !== null
                    ? `${statistics.margenPct.toFixed(1)}%`
                    : "—"}
                </div>
                <div className="mt-2 text-xs text-muted-foreground">
                  Margen bruto / facturación neta
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Chart */}
      {/* {statistics.chartData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">
              Actividad de Facturación
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={statistics.chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="formattedDate"
                  fontSize={12}
                  tickLine={false}
                />
                <YAxis
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(value) => `$${formatCurrency(value)}`}
                />
                <Tooltip
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      return (
                        <div className="rounded-lg border bg-background p-2 shadow-sm">
                          <div className="text-sm font-medium">{label}</div>
                          <div className="text-sm">
                            Documentos: {payload[0]?.payload?.count}
                          </div>
                          <div className="text-sm">
                            Monto: ${formatCurrency(Number(payload[0]?.value))}
                          </div>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="amount"
                  stroke="#2563eb"
                  fill="#3b82f6"
                  fillOpacity={0.1}
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )} */}
    </div>
  );
};

export default FacturasStatistics;
